/**
 * ManageUserCalloutsComponent
 * 
 * This component displays empty-state callouts (informational messages) when no users are found
 * for a selected filter. It listens to grid data load events and filter state changes to show 
 * context-specific messages with action buttons to invite users based on the selected user type filter.
 * 
 * The component is registered as a global page component in the Devware CAP framework,
 * allowing it to be used across the application.
 */

import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { DwComponent, DwComponentType, DwEventService, DwFilterTileChangedEventData, DwFilterTileCountsLoadedEventData, DwFilterTileEvents, DwGridEvents, DwRoutingService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppMetaDataItemNames } from '../../../../meta-data/app-meta-data.service';
import { environment } from '../../../../../environments/environment';

/**
 * Interface for grid data load event data
 * Contains row count information when grid data is loaded
 */
export interface DwGridDataLoadedEventData {
  itemName?: string;                  // The metadata item name (e.g., 'dw_User')
  loadedRowCount?: number;            // Number of rows loaded in the grid
  totalRowCount?: number;             // Total number of rows available
  query?: any;                        // Query object (if available)
  filters?: any;                      // Filters object (if available)
  relations?: any[];                  // Relations array (if available)
  configKeyPart?: string;             // Config key part (if available)
  [key: string]: any;                 // Allow for additional properties
}

/**
 * Devware CAP component decorator
 * Registers this component in the CAP framework as a global page component
 */
@DwComponent({
  key: 'ManageUserCalloutsComponent',
  name: 'Manage User Callouts Component',
  componentType: DwComponentType.pageComponent,
  isGlobal: false
}) 
/**
 * Angular component decorator
 * Defines the component selector and template/style files
 */
@Component({
  selector: 'app-manage-user-callouts',
  templateUrl: './manage-user-callouts.component.html',
  styleUrls: ['./manage-user-callouts.component.scss']
})
export class ManageUserCalloutsComponent implements OnInit, OnDestroy {
  /**
   * CSS variable for overlay left offset; syncs with sidebar/layout so empty-state stays aligned.
   */
  @HostBinding('style.--manage-users-content-left') contentLeft: string;

  /**
   * CSS variable for filter panel offset; shifts callout down when grid filter section is expanded.
   */
  @HostBinding('style.--callout-filter-offset') filterOffset: string = '0px';

  /**
   * CSS variable for aside (config) panel offset; shifts callout right edge inward when aside panel is open.
   */
  @HostBinding('style.--callout-aside-offset') asideOffset: string = '0px';

  /**
   * Controls whether the empty-state callout overlay is visible
   * Set to true when no users are found for the selected filter
   */
  showCallout = false;

  /**
   * The type of callout to display based on the selected user filter
   * Determines which message and action button to show
   */
  calloutType: CalloutType = CalloutType.none;

  /**
   * Current row count from the grid
   * -1 indicates data has not yet loaded
   */
  private currentRowCount: number = -1;

  /**
   * Current detected filter from grid event (e.g., 'students', 'instructors', 'schoolAdmins', 'all')
   */
  private detectedFilter: string | null = null;
  lastFilterTile: any;
  selectedTileKey: string;

  // /**
  //  * Host binding that adds the 'manage-user-callout-active' CSS class to the component
  //  * when showCallout is true. This allows for styling the component based on its active state.
  //  */
  // @HostBinding('class.manage-user-callout-active')
  // get manageUserCalloutActive(): boolean {
  //   return this.showCallout;
  // }

  // /**
  //  * Host binding that adds the 'manage-user-callout-loading' CSS class to the component
  //  * when isLoading is true. This allows for hiding the AG Grid loading overlay.
  //  */
  // @HostBinding('class.manage-user-callout-loading')
  // get manageUserCalloutLoading(): boolean {
  //   return this.isLoading;
  // }

  // /**
  //  * Indicates whether the grid is currently loading data
  //  * true when currentRowCount === -1 (data hasn't loaded yet)
  //  */
  // get isLoading(): boolean {
  //   return this.currentRowCount === -1 && this.isManageUsersRoute();
  // }

  /**
   * Component constructor
   * Injects required services for event handling and routing
   */
  constructor(
    private dwEventService: DwEventService,      // Service for subscribing to framework events
    private dwRoutingService: DwRoutingService,  // Service for navigation/routing
    private router: Router,                      // Angular Router for route checking
    private route: ActivatedRoute               // Activated route for query parameter changes
  ) { }

  /**
   * Subscription to grid data load events
   * Must be unsubscribed in ngOnDestroy to prevent memory leaks
   */
  private gridDataSubscription: Subscription;

  /**
   * Subscription to router events to hide callout when navigating away
   */
  private routerSubscription: Subscription;

  /**
   * Subscription to query parameter changes to detect filter changes
   */
  private queryParamsSubscription: Subscription;

  /**
   * MutationObserver for body class changes (e.g. sidebar-show toggle).
   */
  private bodyClassObserver: MutationObserver;

  /**
   * MutationObserver for html element class changes.
   */
  private htmlClassObserver: MutationObserver;

  /**
   * MutationObserver for .dw-grid-filter2 hidden attribute changes.
   */
  private filterPanelObserver: MutationObserver;

  /**
   * Timeout IDs for scheduled content-left refreshes (sidebar transition timing).
   */
  private refreshContentLeftTimeouts: number[] = [];

  /**
   * Bound transitionend handler for .main margin-left.
   */
  private onMainTransitionEnd: ((e: TransitionEvent) => void) | null = null;

  /**
   * Resize handler; refreshes overlay offset when viewport changes.
   */
  private onResize = (): void => {
    this.refreshContentLeftOffset();
  };

  /**
   * Schedules content-left refresh across sidebar animation (0.25s) so overlay tracks both show and hide.
   */
  private scheduleContentLeftRefresh(): void {
    this.clearScheduledRefresh();
    this.refreshContentLeftOffset();
    requestAnimationFrame(() => this.refreshContentLeftOffset());
    this.refreshContentLeftTimeouts.push(
      window.setTimeout(() => this.refreshContentLeftOffset(), 120),
      window.setTimeout(() => this.refreshContentLeftOffset(), 280)
    );
  }

  /**
   * Clears scheduled refresh timeouts.
   */
  private clearScheduledRefresh(): void {
    this.refreshContentLeftTimeouts.forEach(id => clearTimeout(id));
    this.refreshContentLeftTimeouts = [];
  }

  /**
   * Refreshes --manage-users-content-left from .main margin-left or --dw-sidebar-width fallback.
   */
  private refreshContentLeftOffset(): void {
    const main = document.querySelector('.main');
    if (main) {
      const style = window.getComputedStyle(main);
      const ml = style.getPropertyValue('margin-left');
      const px = parseFloat(ml);
      this.contentLeft = Number.isNaN(px) ? undefined : `${px}px`;

      const mr = style.getPropertyValue('margin-right');
      const mrPx = parseFloat(mr);
      this.asideOffset = Number.isNaN(mrPx) || mrPx <= 0 ? '0px' : `${mrPx}px`;
    } else {
      const root = document.documentElement;
      const dw = root.style.getPropertyValue('--dw-sidebar-width') ||
        getComputedStyle(root).getPropertyValue('--dw-sidebar-width').trim();
      if (dw) {
        this.contentLeft = dw.includes('px') ? dw : `${parseFloat(dw) || 250}px`;
      } else {
        this.contentLeft = undefined;
      }
      this.asideOffset = '0px';
    }
  }

  /**
   * Attaches transitionend listener to .main to finalize offset when sidebar animation completes.
   */
  private attachMainTransitionListener(): void {
    const main = document.querySelector('.main');
    if (main && !this.onMainTransitionEnd) {
      this.onMainTransitionEnd = (e: TransitionEvent): void => {
        if (e.propertyName === 'margin-left' || e.propertyName === 'margin-right') {
          this.refreshContentLeftOffset();
        }
      };
      main.addEventListener('transitionend', this.onMainTransitionEnd as EventListener);
    }
  }

  /**
   * Starts observing the .dw-grid-filter2 element for hidden attribute changes.
   * If the element doesn't exist yet, observes a parent container for child additions.
   */
  private attachFilterPanelObserver(): void {
    const filterPanel = document.querySelector('.dw-grid-filter2') as HTMLElement;
    if (filterPanel) {
      this.observeFilterPanel(filterPanel);
    } else {
      // Element may not exist yet; watch the document body for child additions
      const waitObserver = new MutationObserver(() => {
        const panel = document.querySelector('.dw-grid-filter2') as HTMLElement;
        if (panel) {
          waitObserver.disconnect();
          this.observeFilterPanel(panel);
        }
      });
      waitObserver.observe(document.body, { childList: true, subtree: true });
      // Store so we can clean up if component is destroyed before panel appears
      this.filterPanelObserver = waitObserver;
    }
  }

  /**
   * Observes the filter panel element for hidden attribute changes and measures its height.
   */
  private observeFilterPanel(filterPanel: HTMLElement): void {
    // Measure initial state
    this.updateFilterOffset(filterPanel);

    this.filterPanelObserver = new MutationObserver(() => {
      // Let the DOM settle before measuring
      requestAnimationFrame(() => this.updateFilterOffset(filterPanel));
    });
    this.filterPanelObserver.observe(filterPanel, { attributes: true, attributeFilter: ['hidden'] });
  }

  /**
   * Measures filter panel height and updates the CSS variable offset.
   */
  private updateFilterOffset(filterPanel: HTMLElement): void {
    const isHidden = filterPanel.hasAttribute('hidden');
    if (isHidden) {
      this.filterOffset = '0px';
    } else {
      const height = filterPanel.offsetHeight;
      this.filterOffset = `${height}px`;
    }
  }

  /**
   * Removes transitionend listener from .main.
   */
  private detachMainTransitionListener(): void {
    const main = document.querySelector('.main');
    if (main && this.onMainTransitionEnd) {
      main.removeEventListener('transitionend', this.onMainTransitionEnd as EventListener);
      this.onMainTransitionEnd = null;
    }
  }

  /**
   * Angular lifecycle hook - called when component is initialized
   * Sets up subscriptions to listen for grid data load events and route changes
   */
  ngOnInit(): void {

     // quick feature toggle so we can push to prod before this is complete
    if (!environment.showManageUsersCallout) {
      this.showCallout = false;
      return;
    }

    this.refreshContentLeftOffset();
    window.addEventListener('resize', this.onResize);
    this.attachMainTransitionListener();
    this.bodyClassObserver = new MutationObserver(() => this.scheduleContentLeftRefresh());
    this.bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.htmlClassObserver = new MutationObserver(() => this.scheduleContentLeftRefresh());
    this.htmlClassObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.attachFilterPanelObserver();

    this.gridDataSubscription = this.dwEventService.getEvent(DwFilterTileEvents.filterTileCountsLoaded, true).subscribe((filterTileEvent) => {
      this.handleFilterTileChange(filterTileEvent?.data);
    });

    this.gridDataSubscription = this.dwEventService.getEvent(DwFilterTileEvents.filterTileChanged, true).subscribe((filterTileEvent) => {
      this.handleFilterTileChange(filterTileEvent?.data);
    });

    // // Subscribe to grid data load events
    // // This fires when the AG Grid loads data and tells us the row count
    // // Pass the full event object to inspect for filter/query information
    // this.gridDataSubscription = this.dwEventService.getEvent(DwGridEvents.gridDataLoad, false).subscribe((event) => {
    //   this.handleGridDataLoad(event);
    // });

    // Subscribe to router events to reset state when navigating away from manage-users
    // Also set loading state when route changes (filter changes trigger route updates)
    // this.routerSubscription = this.router.events.pipe(
    //   filter(event => event instanceof NavigationEnd)
    // ).subscribe(() => {
    //   // When route changes (including filter changes), set loading state
    //   if (this.isManageUsersRoute()) {
    //     this.currentRowCount = -1; // Set to loading state
    //   }
    //   this.updateCalloutVisibility();
    // });

    // // Subscribe to query parameter changes to detect filter changes
    // // This catches filter changes that don't trigger NavigationEnd events
    // this.queryParamsSubscription = this.route.queryParams.subscribe(() => {
    //   // When query params change (filter changes), set loading state
    //   if (this.isManageUsersRoute()) {
    //     this.currentRowCount = -1; // Set to loading state
    //     this.updateCalloutVisibility();
    //   }
    // });
  }

  handleFilterTileLoadCounts(filterTileEvent: DwFilterTileCountsLoadedEventData) {

    if (filterTileEvent?.item != AppMetaDataItemNames.DwUser) { 
      return;
    }

    if (this.selectedTileKey) {
      this.lastFilterTile = filterTileEvent?.tileStates?.find(tile => tile.filterTileConfig?.tileKey === this.selectedTileKey);

      this.setupCalloutState();
    }
  }


  handleFilterTileChange(filterTileEvent: DwFilterTileChangedEventData) {
    if (filterTileEvent?.item != AppMetaDataItemNames.DwUser) {
      return;
    }

    if (filterTileEvent?.selectedTileState?.filterTileConfig?.tileKey) {
      this.selectedTileKey = filterTileEvent.selectedTileState.filterTileConfig.tileKey;
      
      this.lastFilterTile = filterTileEvent?.selectedTileState;
    }

    this.setupCalloutState();
    
    return;
  }

  
  private setupCalloutState() {
    
    const metricValue = this.lastFilterTile?.metricValue;
    const normalizedMetricValue = typeof metricValue === 'string' ? metricValue.trim() : metricValue;
    const hasMetricValue = normalizedMetricValue !== null && normalizedMetricValue !== undefined && normalizedMetricValue !== '';
    const metricValueNumber = hasMetricValue ? Number(normalizedMetricValue) : NaN;

    this.showCallout = hasMetricValue && !Number.isNaN(metricValueNumber) && metricValueNumber === 0;

    switch(this.lastFilterTile?.filterTileConfig?.tileKey) {
      case CalloutType.students:
        this.calloutType = CalloutType.students;
        break;
      case CalloutType.instructors:
        this.calloutType = CalloutType.instructors;
        break;
      case CalloutType.schoolAdmins:
        this.calloutType = CalloutType.schoolAdmins;
        break;
      default:
        this.calloutType = CalloutType.all;
        break;
    }
  }

  /**
   * Checks if the current route is the manage-users route
   * This ensures the callout only shows on the Manage Users page
   * Excludes sub-routes like join-requests, edit, assign-courses, etc.
   * 
   * @returns true if the current route is the base manage-users page, false otherwise
   */
  private isManageUsersRoute(): boolean {
    const currentUrl = this.router.url || '';
    
    // Must start with /manage-users
    if (!currentUrl.startsWith('/manage-users')) {
      return false;
    }
    
    // Remove query parameters for path checking
    const urlWithoutQuery = currentUrl.split('?')[0];
    
    // Exact match: /manage-users
    if (urlWithoutQuery === '/manage-users') {
      return true;
    }
    
    // Check if there are path segments after /manage-users/
    // If there are, it's a sub-route and we should exclude it
    const pathAfterBase = urlWithoutQuery.replace('/manage-users', '');
    
    // If there's a path segment (starts with /), it's a sub-route
    if (pathAfterBase.startsWith('/')) {
      // Exclude known sub-routes
      const subRoutePatterns = [
        'join-requests',
        'edit',
        'assign-courses'
      ];
      
      // Check if any sub-route pattern matches
      for (const pattern of subRoutePatterns) {
        if (pathAfterBase.startsWith(`/${pattern}`)) {
          return false;
        }
      }
      
      // If there's any path segment, it's likely a sub-route, exclude it
      return false;
    }
    
    // Only query parameters (no path segments), allow it
    return true;
  }

  /**
   * Handles grid data load events
   * Extracts row count and detects active filter from event data
   * 
   * @param event - The full grid event object (not just event.data)
   */
  private handleGridDataLoad(event: any): void {
    const eventData = event?.data || event;
    
    // Only process events related to DwUser grid (manage-users page)
    if (eventData?.itemName !== AppMetaDataItemNames.DwUser) {
      return;
    }

    // Store the loaded row count
    this.currentRowCount = eventData?.loadedRowCount ?? -1;

    // Detect filter from event data
    this.detectFilterFromEvent(event, eventData);

    // Update visibility based on new data
    this.updateCalloutVisibility();
  }

  /**
   * Detects the active filter from grid event data
   * Checks query relations, filters, and other event properties
   * 
   * @param event - The full grid event object
   * @param eventData - The event data object
   */
  private detectFilterFromEvent(event: any, eventData: DwGridDataLoadedEventData): void {
    // Reset detected filter
    this.detectedFilter = null;

    // Check if event or eventData has query with relations
    const query = event?.query || eventData?.query || event?.data?.query;
    if (query) {
      // Check for relations in the query
      const relations = query.Relations || query.relations || [];
      
      // Check for Student relation
      const hasStudentRelation = relations.some((rel: any) => 
        rel?.RelationName?.toLowerCase().includes('student') ||
        rel?.relationName?.toLowerCase().includes('student')
      );
      
      // Check for Instructor relation
      const hasInstructorRelation = relations.some((rel: any) => 
        rel?.RelationName?.toLowerCase().includes('instructor') ||
        rel?.relationName?.toLowerCase().includes('instructor')
      );
      
      // Check for SchoolAdmin or Admin relation
      const hasAdminRelation = relations.some((rel: any) => 
        rel?.RelationName?.toLowerCase().includes('admin') ||
        rel?.relationName?.toLowerCase().includes('admin') ||
        rel?.RelationName?.toLowerCase().includes('schooladmin') ||
        rel?.relationName?.toLowerCase().includes('schooladmin')
      );

      if (hasStudentRelation && !hasInstructorRelation && !hasAdminRelation) {
        this.detectedFilter = 'students';
        return;
      }
      if (hasInstructorRelation && !hasStudentRelation && !hasAdminRelation) {
        this.detectedFilter = 'instructors';
        return;
      }
      if (hasAdminRelation && !hasStudentRelation && !hasInstructorRelation) {
        this.detectedFilter = 'schoolAdmins';
        return;
      }
    }

    // Check for filters object that might indicate filter type
    const filters = event?.filters || eventData?.filters || event?.data?.filters;
    if (filters) {
      // Check filter keys for indicators
      const filterKeys = Object.keys(filters);
      
      // Look for Student-related filter keys
      if (filterKeys.some(key => key.toLowerCase().includes('student') && !key.toLowerCase().includes('instructor'))) {
        this.detectedFilter = 'students';
        return;
      }
      
      // Look for Instructor-related filter keys
      if (filterKeys.some(key => key.toLowerCase().includes('instructor'))) {
        this.detectedFilter = 'instructors';
        return;
      }
      
      // Look for Admin-related filter keys
      if (filterKeys.some(key => key.toLowerCase().includes('admin') || key.toLowerCase().includes('schooladmin'))) {
        this.detectedFilter = 'schoolAdmins';
        return;
      }
    }

    // Check configKeyPart or view preferences for filter indicators
    const configKeyPart = event?.configKeyPart || eventData?.configKeyPart || event?.data?.configKeyPart;
    if (configKeyPart) {
      const configLower = configKeyPart.toLowerCase();
      if (configLower.includes('student') && !configLower.includes('instructor')) {
        this.detectedFilter = 'students';
        return;
      }
      if (configLower.includes('instructor')) {
        this.detectedFilter = 'instructors';
        return;
      }
      if (configLower.includes('admin') || configLower.includes('schooladmin')) {
        this.detectedFilter = 'schoolAdmins';
        return;
      }
    }

    // If no specific filter detected, default to 'all'
    // Don't set detectedFilter to null here - let it remain null so determineCalloutType can use fallbacks
  }

  /**
   * Updates the callout visibility and type based on current state
   * Shows the callout when on manage-users route and grid has 0 rows
   */
  private updateCalloutVisibility(): void {
    // Only show callout on the manage-users route
    if (!this.isManageUsersRoute()) {
      this.resetCalloutState();
      return;
    }

    // Show callout only when row count is exactly 0
    // Don't show during loading (when currentRowCount is -1)
   // this.showCallout = this.currentRowCount === 0;

    // Determine callout type based on URL parameters or default to 'all'
    //this.determineCalloutType();
  }

  private resetCalloutState(): void {
    this.showCallout = false;
    this.calloutType = CalloutType.none;
    this.currentRowCount = -1;
    this.detectedFilter = null;
    this.lastFilterTile = null;
  }

  /**
   * Determines which type of callout to display
   * Uses priority order: detected filter from grid event > query params > URL matching > default
   */
  private determineCalloutType(): void {
    try {
      // Priority 1: Use detected filter from grid event (most reliable since filters don't change URL)
      if (this.detectedFilter) {
        this.mapTileKeyToCalloutType(this.detectedFilter);
        return;
      }
      
      // Priority 2: Parse query params from URL (fallback for URL-based navigation)
      const currentUrl = this.router?.url || '';
      const urlParams = new URLSearchParams(currentUrl.split('?')[1] || '');
      const tileKeyParam = urlParams.get('tileKey');
      if (tileKeyParam) {
        this.mapTileKeyToCalloutType(tileKeyParam);
        return;
      }
      
      // Priority 3: URL string matching (fallback)
      if (currentUrl.includes('students') || currentUrl.includes('tileKey=students')) {
        this.calloutType = CalloutType.students;
      } else if (currentUrl.includes('instructors') || currentUrl.includes('tileKey=instructors')) {
        this.calloutType = CalloutType.instructors;
      } else if (currentUrl.includes('schoolAdmins') || currentUrl.includes('tileKey=schoolAdmins')) {
        this.calloutType = CalloutType.schoolAdmins;
      } else {
        // Priority 4: Default to 'all' for any other filter type
        this.calloutType = CalloutType.all;
      }
    } catch (error) {
      // If any error occurs, default to 'all' to prevent breaking the page
      console.warn('Error determining callout type:', error);
      this.calloutType = CalloutType.all;
    }
  }

  /**
   * Maps a tile key string to the corresponding CalloutType enum value
   * 
   * @param tileKey - The tile key string (e.g., 'students', 'instructors', 'schoolAdmins')
   */
  private mapTileKeyToCalloutType(tileKey: string): void {
    switch(tileKey) {
      case 'students':
        this.calloutType = CalloutType.students;
        break;
      case 'instructors':
        this.calloutType = CalloutType.instructors;
        break;
      case 'schoolAdmins':
        this.calloutType = CalloutType.schoolAdmins;
        break;
      default:
        this.calloutType = CalloutType.all;
    }
  }

  /**
   * Navigates to the add join request page with appropriate query parameters
   * The query parameter determines which type of user join request to create
   */
  addJoinRequest(): void {
    // Base URL for adding join requests
    let url = '/manage-users/join-requests/add';

    // Add query parameter based on callout type to pre-select the join request type
    // Note: Students (type 1) is the default, so no parameter needed
    switch(this.calloutType) {
      case CalloutType.students:
        // Students use default (joinrequesttypeid=1, implicit)
        break;
      case CalloutType.instructors:
        // Instructors use joinrequesttypeid=2
        url += '?joinrequesttypeid=2';
        break;
      case CalloutType.schoolAdmins:
        // School admins use joinrequesttypeid=3
        url += '?joinrequesttypeid=3';
        break;
      default:
        // All users - no specific type parameter
        break;
    }

    // Navigate to the constructed URL
    this.dwRoutingService.navigateToUrl(url);
  }

  /**
   * Angular lifecycle hook - called when component is destroyed
   * Cleans up subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {

    this.showCallout = false;
    window.removeEventListener('resize', this.onResize);
    this.clearScheduledRefresh();
    this.detachMainTransitionListener();
    if (this.bodyClassObserver) {
      this.bodyClassObserver.disconnect();
    }
    if (this.htmlClassObserver) {
      this.htmlClassObserver.disconnect();
    }
    if (this.filterPanelObserver) {
      this.filterPanelObserver.disconnect();
    }
    if (this.gridDataSubscription) {
      this.gridDataSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }
}

/**
 * Enum defining the types of callouts that can be displayed
 * Each type corresponds to a different user role filter
 */
export enum CalloutType {
  none = 'none',              // No callout displayed
  students = 'students',      // Callout for student filter
  instructors = 'instructors', // Callout for instructor filter
  schoolAdmins = 'schoolAdmins', // Callout for school admin filter
  all = 'all'                 // Callout for all users (default/unspecified filter)
}
